import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Lightbox from './Lightbox';
import ThumbnailPlaceholder from '../utils/ThumbnailPlaceholder';

/* All ten thousand Things, in the site's own grid.
 *
 * Two versions, switched by the links above the grid. The art is served from
 * the Thingdoms S3 buckets, the same URLs the Thingdoms site itself uses, so
 * nothing is duplicated into this repo. The grid pulls the 150px thumbnails
 * and the viewer pulls the full size, which is the only reason ten thousand
 * of anything can sit on one page.
 *
 * Rendering is incremental rather than virtualised: a screenful at a time,
 * more as you reach the bottom. Same behaviour as the Thingdoms gallery.
 */

const TOTAL = 10000;
const FIRST = 60;
const STEP = 60;

const NEW_BUCKET = 'https://new-thingdoms.s3.us-east-1.amazonaws.com';

/* Full size in the grid, not the 150px thumbnails. The Thingdoms site itself
   renders the 1000x1000 art in its gallery, and at six columns a 150px file
   is being stretched to about 200, which is why the small ones looked soft. */
const VERSIONS = [
  {
    key: 'things',
    label: 'Things, 2021',
    src: (id) => `${NEW_BUCKET}/full_1000x1000/original/${id}.png`,
  },
  {
    key: 'pixel',
    label: 'Pixel Thing, 2023',
    src: (id) => `${NEW_BUCKET}/full_1000x1000/pixel/${id}.gif`,
  },
];

/* One tile. A coloured block holds the space until the art arrives, the same
   as the Thingdoms gallery, then gets dropped once the image is painted. */
function ThingTile({ src, onOpen }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className="grid-item" onClick={onOpen}>
      {!loaded && <ThumbnailPlaceholder />}
      <img
        src={src}
        alt=""
        loading="lazy"
        draggable="false"
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
      />
    </div>
  );
}

export default function ThingsGrid() {
  const [versionKey, setVersionKey] = useState('things');
  const [count, setCount] = useState(FIRST);
  const [lightbox, setLightbox] = useState(null);

  const version = VERSIONS.find((v) => v.key === versionKey) || VERSIONS[0];

  // ids are one based, 1 to 10000
  const fulls = useMemo(
    () => Array.from({ length: TOTAL }, (_, i) => version.src(i + 1)),
    [version]
  );

  useEffect(() => {
    function onScroll() {
      if (count >= TOTAL) return;
      const bottom = document.documentElement.scrollHeight - window.innerHeight;
      if (window.scrollY >= bottom - 400) {
        setCount((n) => Math.min(n + STEP, TOTAL));
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();   // in case the first screenful doesn't fill the page
    return () => window.removeEventListener('scroll', onScroll);
  }, [count]);

  const choose = useCallback((key) => {
    setVersionKey(key);
    setLightbox(null);
  }, []);

  return (
    <div className="things-grid-block">
      <ul className="info-links detail-links things-versions">
        {VERSIONS.map((v) => (
          <li key={v.key}>
            <button
              type="button"
              className={'title-link-secondary things-version' + (v.key === versionKey ? ' active' : '')}
              onClick={() => choose(v.key)}
              aria-pressed={v.key === versionKey}
            >
              <span className="title-text-secondary">{v.label}</span>
            </button>
          </li>
        ))}
      </ul>

      <div className="grid-container things-grid">
        {Array.from({ length: count }, (_, i) => (
          <ThingTile
            key={version.key + '-' + (i + 1)}
            src={version.src(i + 1)}
            onOpen={() => setLightbox(i)}
          />
        ))}
      </div>

      {count < TOTAL && (
        <div className="caption-text things-count">
          {count} of {TOTAL.toLocaleString()}
        </div>
      )}

      {lightbox !== null && (
        <Lightbox
          images={fulls}
          index={lightbox}
          onIndex={setLightbox}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
